import React from 'react';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingCoordinatorAddSession from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorAddSession';
import CoachingCoordinatorAddCoachSidebar from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorAddCoachSidebar';
import CoachingCoordinatorPeopleListView from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import CoachingCoordinatorParticipantSidebar from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorParticipantSidebar';
import Container from 'components/Container';
import ErrorReporter from 'app/components/ErrorReporter';
import { CoachingUserType } from 'globalTypes';
import { GetParticipantList_coaching_getParticipantList_participants } from 'coaching/queries/__generated__/GetParticipantList';
import GetCoachingSessionsAndRecordingsContainer from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import GetCreateCoachFieldsContainer from 'coaching/containers/GetCreateCoachFieldsContainer';
import { PeopleViewProps } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorView';

import '../styles/CoachingCoordinatorView.scss';

type State = {
  sidebar: 'participant' | 'coach';
  content: 'list' | 'addSession';
};

export default class CoachingCoordinatorView extends React.Component<PeopleViewProps, State> {
  state: State = {
    sidebar: 'participant',
    content: 'list',
  };

  showScheduleSessionView = () => {
    this.setState({ content: 'addSession' });
  };

  showListView = () => {
    this.setState({ content: 'list' });
  };

  showParticipantSidebar = () => {
    this.setState({ sidebar: 'participant' });
  };

  showCoachSidebar = () => {
    this.setState({ sidebar: 'coach' });
  };

  handleSelectParticipant = (
    participant: GetParticipantList_coaching_getParticipantList_participants
  ) => {
    this.props.onSelectParticipant(participant);
    this.showParticipantSidebar();
  };

  handleListTabsChange = (tabIndex: number) => {
    if (tabIndex === 0) {
      // participants tab
      this.showParticipantSidebar();
    } else if (tabIndex === 1) {
      // coaches tab
      this.showCoachSidebar();
    }
  };

  render() {
    const {
      participants,
      loadingParticipants,
      participantsError,
      coaches,
      loadingCoaches,
      coachesError,
      selectedParticipant,
      onCreateCoachingSession,
      participantPagination,
      coachPagination,
      loadingCancellationReasons,
      cancellationReasonsError,
      cancellationReasons,
    } = this.props;
    if (
      (!participants && loadingParticipants) ||
      (!coaches && loadingCoaches) ||
      (!cancellationReasons && loadingCancellationReasons)
    ) {
      return <CenteredLoadingIndicator />;
    }

    if (participantsError || !participants) {
      return <ErrorReporter error={participantsError ?? 'Could not load coaching participants'} />;
    } else if (coachesError || !coaches) {
      return <ErrorReporter error={coachesError ?? 'Could not load coaching coaches'} />;
    } else if (cancellationReasonsError || !cancellationReasons) {
      return (
        <ErrorReporter error={cancellationReasonsError ?? 'Could not load cancellation reasons'} />
      );
    }

    const contentComponents = {
      list: (
        <CoachingCoordinatorPeopleListView
          loadingParticipants={loadingParticipants}
          participants={participants}
          onSelectParticipant={this.handleSelectParticipant}
          onNextParticipantPage={participantPagination.onNextPage}
          participantListHasNextPage={participantPagination.hasNextPage}
          selectedParticipant={selectedParticipant}
          onListTabsChange={this.handleListTabsChange}
          coaches={coaches}
          loadingCoaches={loadingCoaches}
          onNextCoachPage={coachPagination.onNextPage}
          coachListHasNextPage={coachPagination.hasNextPage}
        />
      ),
      addSession: selectedParticipant && (
        <CoachingCoordinatorAddSession
          participant={selectedParticipant}
          onCreateCoachingSession={onCreateCoachingSession}
          onHide={this.showListView}
          loadingCoaches={loadingCoaches}
          coaches={coaches}
          coachesError={coachesError}
          onNextCoachPage={coachPagination.onNextPage}
          coachListHasNextPage={coachPagination.hasNextPage}
        />
      ),
    };

    const sidebarComponents = {
      participant: selectedParticipant && (
        <GetCoachingSessionsAndRecordingsContainer
          participantUuid={selectedParticipant.uuid}
          userType={CoachingUserType.participant}
          distanceLearningSessionUuid={selectedParticipant.distanceLearningUuid}>
          {({ upcomingSessions, previousSessions, sessionMedia, loading, error }) => {
            return (
              <CoachingCoordinatorParticipantSidebar
                participant={selectedParticipant}
                onShowScheduleSessionView={this.showScheduleSessionView}
                upcomingSessions={upcomingSessions}
                previousSessions={previousSessions}
                sessionMedia={sessionMedia}
                sessionsLoading={loading}
                sessionsError={error}
                cancellationReasons={cancellationReasons}
              />
            );
          }}
        </GetCoachingSessionsAndRecordingsContainer>
      ),
      coach: (
        <GetCreateCoachFieldsContainer>
          {({ subjects, hourlyRatesInCents, error, loading }) => (
            <CoachingCoordinatorAddCoachSidebar
              subjects={subjects}
              hourlyRatesInCents={hourlyRatesInCents}
              error={error?.message}
              loadingCoachableSubjects={loading}
            />
          )}
        </GetCreateCoachFieldsContainer>
      ),
    };

    return (
      <>
        <div className="CoachingCoordinatorView-content">
          <Container size="large">{contentComponents[this.state.content]}</Container>
        </div>
        {this.state.sidebar && (
          <div className="CoachingCoordinatorView-sidebar">
            {sidebarComponents[this.state.sidebar]}
          </div>
        )}
      </>
    );
  }
}
